import React from 'react';
import { Coords, MapTypeStyle } from 'google-map-react';
import { defaultTheme } from '../../themes/theme-registry';

export default interface Config {
  data: Data;
  ui: UI;
  cardContent: CardContent<CardContentConfig>;
  styles: Styles;
  filters: Filters<FilterConfig>;
  i18n: I18n;
  api: Api;
}

export interface Data {
  url?: string;
  language: string;
  fieldsSmall?: boolean;
  maxCacheAge?: string;
}

export interface UI {
  theme: string;
  jobsPerPage: number;
  showEmptyChoices: boolean;
  linkTarget: string;
  sort: Sort;
  showLabels: boolean;
  showIcons: boolean;
  lazyLoading: boolean;
  flagAsNew: number;
}

export interface CardContent<T> {
  categories: T;
  locations: T;
  targetGroups: T;
  workingTimes: T;
  contractPeriod: T;
  earliestEntryDate: T;
  company: T;
  orgUnit: T;
}

export interface CardContentConfig {
  enabled: boolean | undefined;
  order: number | undefined;
}

export const DefaultCardContentConfig: CardContentConfig = {
  enabled: false,
  order: undefined,
};

export type Sort = { by: 'date' | 'title'; order: 'asc' | 'desc' };

export interface Styles {
  brandColor?: string | undefined;
  brandContrastColor?: string | undefined;
  textColor?: string | undefined;
  fontFamily?: string | undefined;
  fontSize?: string | undefined;
  borderRadius?: string | undefined;
  labelStyle?: DefinedStyles | undefined;
  headlineStyle?: DefinedStyles | undefined;
  cardStyle?: DefinedBlockStyles | undefined;
  inputStyle?: DefinedBlockStyles | undefined;
}

export type DefinedStyles =
  | {
      color: string | undefined;
      fontFamily: string | undefined;
      fontSize: string | undefined;
      fontWeight: string | undefined;
    }
  | React.CSSProperties;

export type DefinedBlockStyles =
  | {
      background: string | undefined;
      borderRadius: string | undefined;
      backgroundHover: string | undefined;
    }
  | React.CSSProperties;

export interface I18n {
  messages: Record<string, Record<string, any>>;
}

export interface Api {
  maps: Maps;
}

export interface Maps {
  apiKey?: string;
  requireConsent: boolean;
  privacyPolicyUrl?: string;
  styles?: MapTypeStyle[];
  defaultCenter?: Coords;
}

export interface Filters<T> {
  maps: T;
  keywords: T;
  orgUnits: T;
  companies: T;
  categories: T;
  locations: T;
  targetGroups: T;
  languages: T;
}

export interface FilterConfig {
  enabled: boolean;
  hidden: boolean;
  selected: string[];
  allowed: string[];
  order: number | undefined;
  label: string | undefined;
}

export const DefaultFilterConfig: FilterConfig = {
  enabled: false,
  hidden: false,
  selected: [],
  allowed: [],
  order: undefined,
  label: undefined,
};

export const DefaultConfig: Config = {
  data: {
    language: 'de',
  },
  ui: {
    theme: defaultTheme,
    jobsPerPage: 20,
    showEmptyChoices: false,
    linkTarget: '_blank',
    sort: { by: 'date', order: 'desc' },
    showLabels: true,
    showIcons: false,
    lazyLoading: true,
    flagAsNew: 5,
  },
  cardContent: {
    categories: { ...DefaultCardContentConfig, enabled: true },
    locations: { ...DefaultCardContentConfig, enabled: true },
    targetGroups: { ...DefaultCardContentConfig },
    workingTimes: { ...DefaultCardContentConfig },
    contractPeriod: { ...DefaultCardContentConfig },
    earliestEntryDate: { ...DefaultCardContentConfig },
    company: { ...DefaultCardContentConfig, enabled: true },
    orgUnit: { ...DefaultCardContentConfig },
  },
  styles: {
    brandColor: undefined,
    textColor: undefined,
    fontFamily: undefined,
    fontSize: undefined,
    borderRadius: undefined,
    labelStyle: {},
    headlineStyle: {},
    inputStyle: {},
    cardStyle: {},
  },
  filters: {
    maps: { ...DefaultFilterConfig },
    keywords: { ...DefaultFilterConfig },
    orgUnits: { ...DefaultFilterConfig },
    companies: { ...DefaultFilterConfig },
    categories: { ...DefaultFilterConfig, enabled: true },
    locations: { ...DefaultFilterConfig, enabled: true },
    targetGroups: { ...DefaultFilterConfig, enabled: true },
    languages: { ...DefaultFilterConfig },
  },
  i18n: {
    messages: {},
  },
  api: {
    maps: {
      requireConsent: false,
    },
  },
};
